//src/components/Fields/OrganizationManagedFieldPreview.jsx

import React, { useMemo } from 'react';
import { Typography, Box } from '@mui/material';
import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CategoryIcon from '@mui/icons-material/Category';

import DraggablePreviewWrapper from './DraggablePreviewWrapper';
import { useFormLayout } from '../../contexts/FormLayoutContext';

/**
 * OrganizationManagedFieldPreview
 * -----------------------------------------------------------------------------
 * Preview item used in the Form Builder sidebar for centrally managed org fields.
 *
 * PURPOSE
 *  • Reuse the same drag/preview infrastructure as local fields
 *  • Carry a stable managedFieldId into the drag payload
 *  • Provide a synthetic preview `id` so the existing FormLayoutContext guard
 *    remains intact and we do not need to weaken it
 *
 * IMPORTANT
 *  • `managedFieldId` is the true org-level identity
 *  • `id` here is only the preview/drag item identity
 *  • labels are display-only and are NOT used as identity
 *
 * MVP SCOPE
 *  • Supports managed single-select / multi-select / asset-type previews
 *  • Uses field-type-appropriate icons
 * -----------------------------------------------------------------------------
 */

const resolvePreviewIcon = (fieldType) => {
  switch (fieldType) {
    case 'multi-select':
      return (
        <CheckBoxIcon
          sx={{ fontSize: '0.95rem', marginRight: '8px', color: '#455A64' }}
        />
      );
    case 'asset-type':
      return (
        <CategoryIcon
          sx={{ fontSize: '0.95rem', marginRight: '8px', color: '#455A64' }}
        />
      );
    case 'single-select':
    default:
      return (
        <ArrowDropDownCircleIcon
          sx={{ fontSize: '0.95rem', marginRight: '8px', color: '#455A64' }}
        />
      );
  }
};

const OrganizationManagedFieldPreview = ({
  managedField,
  index = 0,
}) => {
  const { showDropZones, setShowDropZones } = useFormLayout();

  const managedFieldId = managedField?.id || '';
  const fieldType = managedField?.type || 'single-select';
  const label = managedField?.label || 'Managed field';

  // IMPORTANT:
  // Keep the existing FormLayoutContext.handleDrop() guard intact by ensuring
  // every managed preview item has a stable preview id.
  //
  // This is NOT the org identity.
  // The real org identity remains managedFieldId.
  const previewId = useMemo(() => {
    return `managed-preview-${managedFieldId}`;
  }, [managedFieldId]);

  const icon = useMemo(() => resolvePreviewIcon(fieldType), [fieldType]);

  // Optional debug – mirrors your existing preview components
  try {
    setShowDropZones && setShowDropZones.toString();
  } catch (error) {
    console.error(
      '[OrganizationManagedFieldPreview] Error logging setShowDropZones function definition:',
      error,
    );
  }

  return (
    <DraggablePreviewWrapper
      id={previewId}
      type={fieldType}
      index={index}
      isNew={true}
      isManagedFieldPreview={true}
      managedFieldId={managedFieldId}
      managedFieldDefinition={managedField}
      showDropZones={showDropZones}
      setShowDropZones={setShowDropZones}
    >
      <Box display="flex" alignItems="center">
        {icon}
        <Typography
          variant="body2"
          sx={{ flexGrow: 1, fontSize: '0.85rem', color: '#455A64' }}
        >
          {label}
        </Typography>
      </Box>
    </DraggablePreviewWrapper>
  );
};

export default OrganizationManagedFieldPreview;